import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../../styles/ManageCustomersPage.css'; // Ensure this CSS file exists or use Admin.css

const ManageCustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form State
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: { street: '', city: '', state: '', zipCode: '' },
    tags: '',
    notes: ''
  });

  // UI State
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Initial Fetch
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [custRes, billRes] = await Promise.all([
        axios.get('/api/customers'),
        axios.get('/api/bills')
      ]);
      setCustomers(custRes.data);
      setBills(billRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  // Calculate Lifetime Stats
  const getCustomerStats = (customerId) => {
    const customerBills = bills.filter(b =>
      (b.customer && b.customer._id === customerId) ||
      (b.customer === customerId)
    );

    const totalPurchases = customerBills.reduce((sum, b) => sum + (b.totalAmount || 0), 0);
    const billCount = customerBills.length;
    // Pending logic can be refined later based on bill payment history
    const pendingAmount = customerBills.reduce((sum, b) => sum + (b.paymentDetails?.balance || 0), 0);

    return { totalPurchases, billCount, pendingAmount };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        notes: formData.notes,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        customerType: 'regular' // Default
      };

      if (editingId) {
        await axios.put(`/api/customers/${editingId}`, payload);
      } else {
        await axios.post('/api/customers', payload);
      }

      // Reset and Refresh
      fetchData();
      resetForm();
    } catch (error) {
      console.error("Error saving customer:", error);
      alert("Error saving customer. Please check console.");
    }
  };

  const handleEdit = (customer) => {
    setEditingId(customer._id);
    setFormData({
      firstName: customer.firstName,
      lastName: customer.lastName,
      phone: customer.phone,
      email: customer.email || '',
      address: customer.address || { street: '', city: '', state: '', zipCode: '' },
      tags: customer.tags ? customer.tags.join(', ') : '',
      notes: customer.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this customer?")) return;
    try {
      await axios.delete(`/api/customers/${id}`);
      fetchData();
    } catch (error) {
      console.error("Error deleting customer:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '', lastName: '', phone: '', email: '',
      address: { street: '', city: '', state: '', zipCode: '' },
      tags: '', notes: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  if (loading) return <div className="admin-container">Loading...</div>;

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Customer Management</h1>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="btn-primary">
            + Add New Customer
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="dashboard-card" style={{ marginTop: '20px', textAlign: 'left' }}>
          <h2>{editingId ? 'Edit Customer' : 'Add Customer'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group-row" style={{ display: 'flex', gap: '10px' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label>First Name</label>
                <input name="firstName" value={formData.firstName} onChange={handleInputChange} required />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Last Name</label>
                <input name="lastName" value={formData.lastName} onChange={handleInputChange} required />
              </div>
            </div>

            <div className="form-group-row" style={{ display: 'flex', gap: '10px' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Phone</label>
                <input name="phone" value={formData.phone} onChange={handleInputChange} required />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Email</label>
                <input name="email" value={formData.email} onChange={handleInputChange} />
              </div>
            </div>

            <div className="form-group">
              <label>Address (City)</label>
              <input name="address.city" value={formData.address.city} onChange={handleInputChange} />
            </div>

            <div className="form-group">
              <label>Tags (comma separated)</label>
              <input name="tags" value={formData.tags} onChange={handleInputChange} placeholder="e.g. Wedding, Investor, VIP" />
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows="3" />
            </div>

            <div style={{ marginTop: '15px' }}>
              <button type="submit" className="btn-primary" style={{ marginRight: '10px' }}>Save</button>
              <button type="button" onClick={resetForm} className="btn-secondary">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Data Table */}
      <div className="table-responsive">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Contact</th>
              <th>City</th>
              <th>Total Bills</th>
              <th>Total Purchased</th>
              <th>Pending Dues</th>
              <th>Last Bill</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.map(customer => {
              const stats = getCustomerStats(customer._id);
              const lastBillDate = customer.lastBillDate ? new Date(customer.lastBillDate).toLocaleDateString() : '-';

              return (
                <tr key={customer._id}>
                  <td>
                    <div style={{ fontWeight: 'bold' }}>{customer.firstName} {customer.lastName}</div>
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>{customer.tags && customer.tags.length > 0 ? customer.tags.join(', ') : 'Regular'}</div>
                  </td>
                  <td>
                    <div>{customer.phone}</div>
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>{customer.email}</div>
                  </td>
                  <td>{customer.address?.city || '-'}</td>
                  <td>{stats.billCount}</td>
                  <td>₹{stats.totalPurchases.toLocaleString()}</td>
                  <td>
                    {stats.pendingAmount > 0 ? (
                      <span className="badge badge-danger">₹{stats.pendingAmount.toLocaleString()}</span>
                    ) : (
                      <span className="badge badge-success">Paid</span>
                    )}
                  </td>
                  <td>{lastBillDate}</td>
                  <td>
                    <button onClick={() => handleEdit(customer)} className="btn-secondary" style={{ marginRight: '5px', padding: '4px 8px', fontSize: '0.85rem' }}>Edit</button>
                    <button onClick={() => handleDelete(customer._id)} className="btn-danger" style={{ padding: '4px 8px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem' }}>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageCustomersPage;
